class AgoraSingleton {
  constructor() {
    if (!AgoraSingleton.instance) {
      this.client = null
      this.appId = null
      this.channelName = null
      this.token = null
      this.tracks = null
      this.start = false
      this.users = []
      this.remoteConnectionState = { audio: false, video: false }
      this.time = 0
      this.ready = false
      this.initialName = null
      this.currentOption = null
      this.isRouteChanged = false
      this.inCall = false
      this.showModal = false
      this.selectedCallType = null
      this.observers = {} // Generic observers
      this.timer = null
      this.isEnd = false
      this.isMicMuted = false // Mic mute state
      this.isCameraOff = false // Camera state

      AgoraSingleton.instance = this
      return this;
    }

    return AgoraSingleton.instance
  }

  static getInstance() {
    if (!AgoraSingleton.instance) {
      AgoraSingleton.instance = new AgoraSingleton()
    }
    return AgoraSingleton.instance
  }

  addObserver(type, callback) {
    if (!(type in this.observers)) {
      this.observers[type] = []
    }
    this.observers[type].push(callback)
  }

  removeObserver(type, callback) {
    if (type in this.observers) {
      this.observers[type] = this.observers[type].filter(
        (observer) => observer !== callback,
      )
    }
  }

  notifyObservers(type, data) {
    if (type in this.observers) {
      this.observers[type].forEach((callback) => callback(data))
    }
  }

  toggleMicMute = async () => {
    if (this.selectedCallType === 'videoCall') {
      if (this.tracks && this.tracks[0]) {
        this.isMicMuted = !this.isMicMuted // Toggle mic mute state
        await this.tracks[0].setEnabled(!this.isMicMuted) // Enable/disable mic
        this.notifyObservers('isMicMuted', this.isMicMuted) // Notify observers of the change
      }
    }
    if (this.selectedCallType === 'audioCall') {
      if (this.tracks) {
        this.isMicMuted = !this.isMicMuted // Toggle mic mute state
        await this.tracks.setEnabled(!this.isMicMuted) // Enable/disable mic
        this.notifyObservers('isMicMuted', this.isMicMuted) // Notify observers of the change
      }
    }
  }

  toggleCamera = async () => {
    if (this.tracks && this.tracks[1]) {
      this.isCameraOff = !this.isCameraOff // Toggle camera state
      await this.tracks[1].setEnabled(!this.isCameraOff) // Enable/disable camera
      this.notifyObservers('isCameraOff', this.isCameraOff) // Notify observers of the change
    }
  }

  setUsers(users) {
    try {
      this.users = users
      this.notifyObservers('users', users)
    } catch (e) {}
  }

  setStart(start) {
    if (this.start !== start) {
      this.start = start
      this.notifyObservers('start', start)
    }
  }

  setInCall(inCall) {
    if (this.inCall !== inCall) {
      this.inCall = inCall
      this.notifyObservers('inCall', inCall)
    }
  }

  setRemoteConnectionState(state) {
    if (this.remoteConnectionState !== state) {
      this.remoteConnectionState = state
      this.notifyObservers('remoteConnectionState', state)
    }
  }

  setCallType(callType) {
    if (this.selectedCallType !== callType) {
      this.selectedCallType = callType
      this.notifyObservers('callType', callType)
    }
  }

  handleTrack = (tracks) => {
    if (this.tracks !== tracks) {
      this.tracks = tracks
      this.notifyObservers('tracks', tracks)
    }
  }

  startTimer() {
    if (this.timer) clearInterval(this.timer)
    this.timer = setInterval(() => {
      this.time += 1
      this.notifyObservers('time', this.time)
    }, 1000)
  }

  stopTimer() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
      this.time = 0 // Reset time if needed
      this.notifyObservers('time', this.time)
    }
  }

  async init(name, setCallJoiningStatus) {
    console.log("Agorraaa init", this.client, this.appId, this.channelName, this.token, this.account)
    if (!this.client || !this.appId || !this.channelName || !this.token) {
      console.error('Missing required parameters for Agora initialization')
      return
    }

    if (this.selectedCallType === 'videoCall') {
      try {
        await this.client.join(
          this.appId,
          this.channelName,
          this.token,
          this.account,
        )

        if (this.tracks) {
          await this.client.publish([this.tracks[0], this.tracks[1]])
        }
        this.setStart(true)
        if(setCallJoiningStatus){
          setCallJoiningStatus(true)
        }
        if (this.token) {
          this.setInCall(true)
        }

        this.client.on('user-published', async (user, mediaType) => {
          try {
            this.startTimer()

            await this.client.subscribe(user, mediaType)
            let oldUsers = this.users.filter((User) => User.uid !== user.uid)
            this.setUsers([...oldUsers, user])

            if (mediaType === 'audio') {
              user.audioTrack?.play()
            }

            this.setRemoteConnectionState((prevState) => {
              let newState = { ...prevState }
              newState[mediaType] = true
              // this.manageCall(obj)
              return newState
            })
          } catch (error) {
            console.error('Error subscribing to user:', error)
          }
        })

        this.client.on('user-unpublished', (user, type) => {
          if (type === 'audio') {
            user.audioTrack?.stop()
          }

          this.setRemoteConnectionState((prevState) => {
            let newState = { ...prevState }
            newState[type] = false
            return newState
          })
        })

        this.client.on('user-left', (user) => {
          this.setUsers(this.users.filter((User) => User.uid !== user.uid))
          if (user.audioTrack) {
            user.audioTrack?.stop()
          }
          this.setRemoteConnectionState({ audio: false, video: false })
        })
      } catch (error) {
        console.log('error in init', error)
        if (
          error.name === 'AgoraRTCException' &&
          error.message.includes('cancel token canceled')
        ) {
          console.warn('Operation canceled:', error)
        } else {
          console.error('Error while initiating call:', error)
        }
      }
    }

    if (this.selectedCallType === 'audioCall') {
      try {
        console.log('Initializing audio call:', {appId: this.appId, channelName: this.channelName, token: this.token, account: this.account, name})
        await this.client.join(
          this.appId,
          this.channelName,
          this.token,
          this.account,
        )

        if (this.tracks) {
          // Publish only the audio track
          await this.client.publish([this.tracks])
          await this.tracks.setEnabled(true) // Ensure audio is enabled
        }

        this.setStart(true)
        if (this.token) {
          this.setInCall(true)
        }

        this.client.on('user-published', async (user, mediaType) => {
          this.startTimer()
          console.log('User published in audio call:', user, mediaType)

          await this.client.subscribe(user, mediaType)

          // Update users list
          let oldUsers = this.users.filter((User) => User.uid !== user.uid)
          this.setUsers([...oldUsers, user])

          if (mediaType === 'audio') {
            user.audioTrack?.play() // Play the audio track
          }

          this.setRemoteConnectionState((prevState) => {
            let newState = { ...prevState }
            newState[mediaType] = true
            return newState
          })
        })

        this.client.on('user-unpublished', (user, type) => {
          console.log('User unpublished in audio call:', user, type)
          this.setRemoteConnectionState((prevState) => {
            let newState = { ...prevState }
            newState[type] = false
            return newState
          })

          if (type === 'audio') {
            user.audioTrack?.stop() // Stop the audio track
          }
        })

        this.client.on('user-left', (user) => {
          console.log('User left the audio call:', user)
          this.setUsers(this.users.filter((User) => User.uid !== user.uid))
          this.setRemoteConnectionState({ audio: false, video: false })
        })
      } catch (error) {
        console.log('Error in audio call init:', error)
      }
    }
  }

  handleEndCall(end, setCallJoiningStatus) {
    // if (end !== this.isEnd) {
      // this.isEnd = end
      // this.start = false
      
      this.stopTimer() // Ensure the timer stops
      let currentClient = this.client
      // this.client = null;

      // Clean up tracks and Agora client resources
      if (this.selectedCallType === 'videoCall' && this.tracks) {
        // this.tracks.forEach((track) => {
        //   if(track){
        //     track.stop() // Stop each track
        //     track.close() // Close each track
        //   }
          
        // })

        this.tracks = null // Reset tracks
      } else {
        if(this.tracks){
          console.log('stopping audio track', this.tracks)
          
          // if(!this.tracks?._isClosed){
          //   this.tracks.stop() // Stop the audio track
          //   this.tracks.close() // Close the audio track

          // }
        }
        this.tracks = null // Reset the
      }

      if (currentClient) {
        currentClient.removeAllListeners() // Clean up listeners

        currentClient
          .leave()
          .then(() => {
            currentClient = null
            this.client = null
            if(setCallJoiningStatus){
              setCallJoiningStatus(false)
            }
          })
          .catch((error) => {
            console.error('Error while leaving Agora client:', error)
          })
      }

      // Reset users and connection state
      // this.users = []
      // this.remoteConnectionState = { audio: false, video: false }
      // this.ready = false

      // Notify all observers of the changes
      // this.notifyObservers('endCall', end)
      // this.notifyObservers('start', false) // Set start to false
      // this.notifyObservers('users', this.users)
      // this.notifyObservers('remoteConnectionState', this.remoteConnectionState)
      // this.notifyObservers('ready', this.ready)

      this.destroyInstance() // Destroy the singleton instance
    // }
  }

  destroyInstance() {
    // Nullify all properties to release memory
    // this.client = null
    this.appId = null
    this.channelName = null
    this.token = null
    this.tracks = null
    this.start = false
    this.users = []
    this.remoteConnectionState = { audio: false, video: false }
    this.time = 0
    this.ready = false
    this.initialName = null
    this.currentOption = null
    this.isRouteChanged = false
    this.inCall = false
    this.showModal = false
    this.selectedCallType = null
    this.observers = {} // Generic observers
    this.timer = null
    this.isEnd = false
    this.isMicMuted = false // Mic mute state
    this.isCameraOff = false // Camera state

    // Nullify the singleton instance itself
    //AgoraSingleton.instance = null
  }

  shallowEqual = (array1, array2) => {
    if (array1.length !== array2.length) return false
    for (let i = 0; i < array1.length; i++) {
      if (array1[i].uid !== array2[i].uid) return false
    }
    return true
  }

  handleRouteChange(routeChange) {
    if (this.isRouteChanged !== routeChange) {
      this.isRouteChanged = routeChange
      this.notifyObservers('isRouteChanged', routeChange)
    }
  }

  handleCurrentOption(currentOption) {
    if (this.currentOption !== currentOption) {
      this.currentOption = currentOption
      this.notifyObservers('currentOption', currentOption)
    }
  }
  resetCurrentOption() {
    if (this.currentOption) {
      this.currentOption = null
      this.removeObserver('resest currentOption ', this.currentOption)
    }
  }

  handleInitialName(name) {
    if (this.initialName !== name) {
      this.initialName = name
      this.notifyObservers('initialName', name)
    }
  }

  handleReady = (ready) => {
    if (this.ready !== ready) {
      this.ready = ready
      this.notifyObservers('ready', ready)
    }
  }

  


  

  
}

export default AgoraSingleton
